import React, { useContext } from 'react'
import { Route, Routes, Navigate, useLocation } from 'react-router-dom'

import Profile from './profile'
import Organizations from './organizations'
import OrganizationDetails from './organizations/details'
import CreateOrganization from './organizations/create'
import Incidents from './incidents'
import IncidentDetails from './incidents/details'
import CreateIncident from './incidents/create'
import CreateIncidentAttachment from './incidents/attachment/create'
import AddDispatchOrganization from './incidents/add-dispatch-organization'
import CCTVEventDetails from './incidents/cctv-event'
import UserDetails from './users'
import CreateUser from './users/create'
import CreateUserRoleBinding from './users/create-user-role-binding'
import CreateUserGroupBinding from './users/create-user-group-binding'
import SetUserType from './users/set-user-type'
import CreateUserRole from './user-roles/create'
import CreateUserRoleGrant from './user-role-grants/create'
import UserRoleDetails from './user-roles/details'
import CopyUserRole from './user-roles/copy'
import CreateUserGroup from './user-groups/create'
import Sites from './sites'
import SiteDetails from './sites/details'
import CreateSite from './sites/create'
import CreateSiteInfo from './sites/info/create'
import CreateSiteOrganizationBinding from './sites/organization-bindings/create'
import Agents from './agents'
import AgentDetails from './agents/details'
import CreateAgent from './agents/create'
import System from './system'
import CreateUserType from './system/user-types/create'
import UserTypeDetails from './system/user-types/details'
import Dispatch from './dispatch'
import CreateDispatchBinding from './dispatch-bindings/create'
import DataExportJobsList from './data-export'
import DataRetentionEvents from './data-retention-events'
import Chat from './chat'
import Login from '../components/Login'
import { ApiContext } from '../hooks/api'
import Signup from '../components/Signup'
import ForgotPassword from '../components/ForgotPassword'
import MyAccount from '../components/MyAccout'
import OtpVerificationScreen from '../components/verify-otp'
import Spinner from '../components/Spinner'
import InviteSignup from '../components/InviteSignup'
import MFASetup from '../components/MfaSetup'
import ResetAuth from '../components/ResetAuth'
import ResetPassword from '../components/ResetPassword'
import { auth } from '../firebase'

function ProtectedRoute({ children }) {
  const { authUser, user, authLoading } = useContext(ApiContext)
  const location = useLocation()
  const pathname = location.pathname
  const mfaInProgress = localStorage.getItem('mfaInProgress') === 'true'
  const mfaUserId = localStorage.getItem('mfaUserId')

  if (authLoading) return <Spinner />

  const effectiveUser = authUser || auth.currentUser

  if (!effectiveUser) {
    return <Navigate to='/login' replace />
  }

  if (pathname === '/mfa-setup' || pathname === '/verify-otp') {
    if (mfaInProgress && mfaUserId) return children
    return <Navigate to='/login' replace />
  }

  if (mfaInProgress) {
    return (
      <Navigate
        to={user?.isTwoFactorEnabled ? '/verify-otp' : '/mfa-setup'}
        replace
      />
    )
  }

  return children
}

export default function () {
  const { user } = useContext(ApiContext)
  return (
    <Routes>
      <Route exact path='/login' element={<Login />} />
      <Route exact path='/signup' element={<Signup />} />
      <Route exact path='/forgot-password' element={<ForgotPassword />} />
      <Route exact path='/reset-password' element={<ResetPassword />} />
      <Route path='/invite/:token' element={<InviteSignup />} />
      <Route path='/reset-authentication' element={<ResetAuth />} />
      <Route
        exact
        path='/mfa-setup'
        element={
          <ProtectedRoute>
            <MFASetup />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/verify-otp'
        element={
          <ProtectedRoute>
            <OtpVerificationScreen />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/profile'
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/organizations/list'
        element={
          <ProtectedRoute>
            <Organizations orgId={user?.orgId} />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/organizations/create'
        element={
          <ProtectedRoute>
            <CreateOrganization />
          </ProtectedRoute>
        }
      />
      <Route
        path='/organizations/:id'
        element={
          <ProtectedRoute>
            <OrganizationDetails />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/users/create'
        element={
          <ProtectedRoute>
            <CreateUser />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/user-roles/create'
        element={
          <ProtectedRoute>
            <CreateUserRole />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/user-roles/:id'
        element={
          <ProtectedRoute>
            <UserRoleDetails />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/user-roles/copy/:id'
        element={
          <ProtectedRoute>
            <CopyUserRole />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/user-role-grants/:id'
        element={
          <ProtectedRoute>
            <CreateUserRoleGrant />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/users/:id/create-role-binding'
        element={
          <ProtectedRoute>
            <CreateUserRoleBinding />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/users/:id/create-group-binding'
        element={
          <ProtectedRoute>
            <CreateUserGroupBinding />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/user-groups/create'
        element={
          <ProtectedRoute>
            <CreateUserGroup />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/users/:id/set-user-type'
        element={
          <ProtectedRoute>
            <SetUserType />
          </ProtectedRoute>
        }
      />
      <Route
        path='/users/:id'
        element={
          <ProtectedRoute>
            <UserDetails />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/incidents/list'
        element={
          <ProtectedRoute>
            <Incidents />
          </ProtectedRoute>
        }
      />
      <Route exact index path='/' element={<Navigate to='/dispatch' />} />
      <Route
        path='/incidents/:id/cctv-events/:eventId'
        element={
          <ProtectedRoute>
            <CCTVEventDetails />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/incidents/create'
        element={
          <ProtectedRoute>
            <CreateIncident />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/incidents/attachment/create'
        element={
          <ProtectedRoute>
            <CreateIncidentAttachment />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/incidents/add-dispatch-organization'
        element={
          <ProtectedRoute>
            <AddDispatchOrganization />
          </ProtectedRoute>
        }
      />
      <Route
        path='/incidents/:id'
        element={
          <ProtectedRoute>
            <IncidentDetails />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/sites/list'
        element={
          <ProtectedRoute>
            <Sites />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/sites/create'
        element={
          <ProtectedRoute>
            <CreateSite />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/sites/info/create'
        element={
          <ProtectedRoute>
            <CreateSiteInfo />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/sites/organization-binding/create'
        element={
          <ProtectedRoute>
            <CreateSiteOrganizationBinding />
          </ProtectedRoute>
        }
      />
      <Route
        path='/sites/:id'
        element={
          <ProtectedRoute>
            <SiteDetails />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/agents/list'
        element={
          <ProtectedRoute>
            <Agents />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/agents/create'
        element={
          <ProtectedRoute>
            <CreateAgent />
          </ProtectedRoute>
        }
      />
      <Route
        path='/agents/:id'
        element={
          <ProtectedRoute>
            <AgentDetails />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/system'
        element={
          <ProtectedRoute>
            <System />
          </ProtectedRoute>
        }
      />
      <Route
        exact
        path='/system/user-types/create'
        element={
          <ProtectedRoute>
            <CreateUserType />
          </ProtectedRoute>
        }
      />
      <Route
        path='/system/user-types/:id'
        element={
          <ProtectedRoute>
            <UserTypeDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path='/dispatch'
        element={
          <ProtectedRoute>
            <Dispatch />
          </ProtectedRoute>
        }
      />
      <Route
        path='/dispatch-bindings/create'
        element={
          <ProtectedRoute>
            <CreateDispatchBinding />
          </ProtectedRoute>
        }
      />
      <Route
        path='/data-export/jobs'
        element={
          <ProtectedRoute>
            <DataExportJobsList />
          </ProtectedRoute>
        }
      />
      <Route
        path='/data-retention-events'
        element={
          <ProtectedRoute>
            <DataRetentionEvents />
          </ProtectedRoute>
        }
      />
      <Route
        path='/chat'
        element={
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        }
      />
      <Route
        path='/my-account'
        element={
          <ProtectedRoute>
            <MyAccount />
          </ProtectedRoute>
        }
      />
      <Route element={<div>Route not found</div>} />
    </Routes>
  )
}
